/**
 * Reactive variable store for the voice dictation feature.
 *
 * Manages dictation lifecycle state across the intake form screen.
 * All state resets when the intake screen unmounts.
 * @module features/voice-report/stores/dictationStore
 */

import { makeVar } from "@apollo/client";

/** Dictation lifecycle states */
export type DictationStatus =
  | "idle"
  | "recording"
  | "processing"
  | "completed"
  | "error";

/** Current dictation status */
export const dictationStatusVar = makeVar<DictationStatus>("idle");

/** Structured form data extracted from dictation (null until completed) */
export const dictationResultVar = makeVar<Record<string, unknown> | null>(null);

/** Raw transcript from transcription (null until completed) */
export const dictationTranscriptVar = makeVar<string | null>(null);

/** Keys of form fields that were AI-filled (for blue border tracking) */
export const dictationFilledFieldsVar = makeVar<readonly string[]>([]);

/** Processing status message for UI ("Transcribing...", "Extracting...") */
export const dictationProcessingMessageVar = makeVar<string>("");

/** Elapsed recording time in seconds */
export const dictationElapsedSecondsVar = makeVar<number>(0);

/** Live transcript from speech recognition during recording */
export const dictationLiveTranscriptVar = makeVar<string>("");

/**
 * Resets all dictation state to initial values.
 * Called on intake screen unmount or when starting fresh.
 */
export const resetDictationState = (): void => {
  dictationStatusVar("idle");
  dictationResultVar(null);
  dictationTranscriptVar(null);
  dictationFilledFieldsVar([]);
  dictationProcessingMessageVar("");
  dictationElapsedSecondsVar(0);
  dictationLiveTranscriptVar("");
};
